import {
  AutocompleteOwnerState,
  AutocompleteRenderOptionState,
  AutocompleteValue,
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  styled,
  ChipTypeMap
} from '@mui/material';
import { FunctionComponent } from 'react';
import * as React from 'react';
import clsx from 'clsx';
import styles from './Autocomplete.module.scss';
import { TextField, TextFieldProps } from '../TextField/TextField';
import { Icon } from '../../icon/Icon';
import { NodeLogo } from '../../shared/components/NodeLogo/NodeLogo';
import { Checkbox } from '../../checkbox/Checkbox';

export type AutocompleteProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent']
> = Omit<
  MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
  'id' | 'renderInput'
> & {
  dataId: string;
  selectMode?: boolean;
  textFieldProps?: Pick<
    TextFieldProps,
    | 'prefixIcon'
    | 'suffixIcon'
    | 'prefixText'
    | 'prefixElement'
    | 'label'
    | 'placeholder'
    | 'suffixElement'
    | 'className'
    | 'error'
    | 'size'
  > & { InputProps?: object };
};

export const Autocomplete = styled(
  <
    A,
    B extends boolean | undefined,
    C extends boolean | undefined,
    D extends boolean | undefined,
    E extends React.ElementType = ChipTypeMap['defaultComponent']
  >({
    dataId,
    textFieldProps,
    selectMode = false,
    value,
    ...props
  }: AutocompleteProps<A, B, C, D, E>) => {
    const sanitizeSelectedValue = (
      selected: AutocompleteValue<A, B, C, D> | undefined,
      multiple: boolean
    ) =>
      multiple && !selected
        ? ([] as AutocompleteValue<A, B, C, D>)
        : selected ?? ('' as AutocompleteValue<A, B, C, D>);

    return (
      <MuiAutocomplete
        renderOption={RenderOption}
        {...props}
        id={dataId}
        value={sanitizeSelectedValue(value, !!props.multiple)}
        data-id={dataId}
        popupIcon={<Icon name='dropdown-arrow' dataId={`${dataId}-open-icon`} />}
        disableCloseOnSelect={!!props.multiple}
        clearIcon={<Icon name='close' />}
        slotProps={{
          paper: {
            sx: {
              boxShadow: 'var(--shadow-dropdown)',
              borderRadius: 'var(--border-radius-6)',
              backgroundColor: 'var(--surface-bg-color)',
              display: 'block',
              '.MuiAutocomplete-listbox .MuiAutocomplete-option': {
                color: 'var(--text-default-color)',
                fontWeight: 'var(--font-weight-medium)',
                '&.Mui-focused': {
                  backgroundColor: 'var(--hover-bg-color)',
                  '&[aria-selected="true"]': {
                    backgroundColor: 'var(--primary-faded-color)'
                  }
                }
              }
            }
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            {...textFieldProps}
            className={clsx({ 'TextField-visuallyHidden': selectMode }, textFieldProps?.className)}
            inputProps={{
              ...params.inputProps
            }}
            dataId={dataId}
          />
        )}
      />
    );
  }
)({
  '.MuiOutlinedInput-root, .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    padding: '0px var(--spacer-2)',
    flexWrap: 'wrap'
  },
  '.MuiOutlinedInput-root': {
    '.MuiAutocomplete-input': {
      padding: 'var(--spacer-16)',

      '&.MuiInputBase-inputSizeSmall': {
        padding: 'var(--spacer-12) var(--spacer-12)'
      }
    },
    '&.MuiInputBase-sizeSmall': {
      '.MuiAutocomplete-input': {
        padding: 'var(--spacer-8) var(--spacer-12)'
      }
    }
  },

  '.MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
    right: 'var(--spacer-16)',
    zIndex: 3
  },
  '.MuiOutlinedInput-root.MuiInputBase-sizeSmall.MuiInputBase-adornedStart': {
    paddingLeft: 'var(--spacer-16)'
  },
  '&.MuiAutocomplete-hasPopupIcon .MuiInputBase-adornedEnd.MuiOutlinedInput-root': {
    paddingRight: '44px'
  },
  '.MuiAutocomplete-clearIndicator': {
    padding: '2px'
  },
  '.TextField-visuallyHidden': {
    opacity: 0,
    transform: 'scale(0)',
    height: '0px',
    display: 'block',
    margin: '0px !important'
  }
});

function RenderOption<
  A,
  B extends boolean | undefined,
  C extends boolean | undefined,
  D extends boolean | undefined,
  E extends React.ElementType
>(
  props: React.HTMLAttributes<HTMLLIElement> & { id?: string; className?: string },
  option: A,
  state: AutocompleteRenderOptionState,
  _ownerState: AutocompleteOwnerState<A, B, C, D, E>
) {
  const {
    label,
    LabelComponent,
    logo = null,
    icon = null,
    description
  } = option as {
    label: string;
    LabelComponent?: FunctionComponent;
    icon?: string;
    description?: string;
    logo?: { logo: string; logoDarkMode: string };
  };

  const { className, ...rest } = props;
  if (_ownerState.multiple) {
    return (
      <li {...rest} className={clsx(styles.renderOption, className)}>
        <div className='center-flex-row w-100'>
          <Checkbox
            id={rest.id}
            dataId={rest.id as string}
            checked={state.selected}
            classes={{
              root: 'mr-1 flex-shrink-0'
            }}
          />

          {logo ? (
            <NodeLogo
              size={1}
              logoURL={logo.logo}
              darkModeLogoURL={logo.logoDarkMode}
              className='mr-2'
            />
          ) : null}

          <p className='text-ellipsis'>{LabelComponent ? <LabelComponent /> : label}</p>
        </div>
      </li>
    );
  }
  return (
    <li
      {...rest}
      className={clsx(styles.renderOption, className, state.selected && 'text-primary')}
    >
      <div className='center-flex-row w-100'>
        {icon ? <Icon name={icon} dataId={`${icon}-icon`} className='mr-2' /> : null}
        {logo ? (
          <NodeLogo
            size={1}
            logoURL={logo.logo}
            darkModeLogoURL={logo.logoDarkMode}
            className='mr-2'
          />
        ) : null}

        <div className='flex-col'>
          <p className='text-ellipsis'>{LabelComponent ? <LabelComponent /> : label}</p>

          {description ? <span className='text-secondary text-normal'>{description}</span> : null}
        </div>
      </div>

      {state.selected ? (
        <Icon
          name='checked-tick'
          dataId='checked-tick-icon'
          className={styles.successCheckedTick}
        />
      ) : null}
    </li>
  );
}
